/**
 * Public (browser-safe) configuration, fetched at runtime from `GET /api/config`.
 *
 * These values used to be hardcoded literals in this file. That was a bug: Bun's
 * bundler only inlines NODE_ENV, so a literal here cannot be overridden per
 * environment — setting an env var on Render changed nothing. The consequence was
 * that every build, including production, shipped a Clerk *development* publishable
 * key (`pk_test_…` → valid-marmoset-95.clerk.accounts.dev) and the staging PostHog
 * project key.
 *
 * Fetching at runtime means one env-var change swaps dev → staging → production with
 * no rebuild, and the same artifact is promotable across environments.
 *
 * ONLY browser-safe values belong here. Server secrets must never be added to
 * `/api/config` — see apps/gfgf-api/src/routes/configRoutes.ts for the allow-list and
 * the reasoning for each entry.
 *
 * `loadPublicEnv()` MUST resolve before the React tree renders; see src/index.tsx.
 * Reading `PUBLIC_ENV` before then yields empty strings.
 */

import { API_BASE_URL } from "./endpoints";

export interface PublicEnv {
  CLERK_PUBLISHABLE_KEY: string;
  POSTHOG_KEY: string;
  POSTHOG_HOST: string;
  ONESCHEMA_CLIENT_ID: string;
  ONESCHEMA_TEMPLATE_STARTER: string;
  ONESCHEMA_TEMPLATE_PRO: string;
  /** Runtime environment name reported by the API ("production" | "staging" | "development").
   *  Fetched (not build-time inlined) so a promoted artifact reflects the env it runs in. */
  NODE_ENV: string;
  /** Google Tag Manager container id (e.g. "GTM-XXXX"). Set in production and on staging (GTM
   *  Preview checks); empty everywhere else, so GTM never loads in dev or local. */
  GTM_CONTAINER_ID: string;
  /** Whether the API has the Ask Cora chat switched on (CORA_ENABLED). False until the load resolves. */
  CORA_ENABLED: boolean;
}

/**
 * Populated in place by `loadPublicEnv()` so existing `PUBLIC_ENV.X` call sites keep
 * working unchanged. Empty strings until the load resolves.
 */
export const PUBLIC_ENV: PublicEnv = {
  CLERK_PUBLISHABLE_KEY: "",
  POSTHOG_KEY: "",
  POSTHOG_HOST: "",
  ONESCHEMA_CLIENT_ID: "",
  ONESCHEMA_TEMPLATE_STARTER: "",
  ONESCHEMA_TEMPLATE_PRO: "",
  NODE_ENV: "",
  GTM_CONTAINER_ID: "",
  CORA_ENABLED: false,
};

/**
 * True only when the API reports it is running in production. Read at CALL time (never at module
 * load): PUBLIC_ENV is populated by loadPublicEnv() after import, so a top-level `const` would
 * capture the empty pre-load value. Safe from any component render, which runs after loadPublicEnv
 * resolves (see src/index.tsx). Used to hide internal-only UI (e.g. the Help Center) in production.
 */
export function isProduction(): boolean {
  return PUBLIC_ENV.NODE_ENV === "production";
}

/**
 * True when the API reports the Ask Cora chat is switched on, and never in production. Read at CALL
 * time for the same reason as isProduction(): PUBLIC_ENV is populated by loadPublicEnv() after
 * import. Gates the chat's entry points so the UI never offers a chat the API would 404.
 */
export function isCoraEnabled(): boolean {
  return !isProduction() && PUBLIC_ENV.CORA_ENABLED;
}

/** Wire shape of GET /api/config. All fields are nullable server-side. */
interface ConfigResponse {
  clerkPublishableKey: string | null;
  posthogKey: string | null;
  posthogHost: string | null;
  oneschemaClientId: string | null;
  oneschemaTemplateStarter: string | null;
  oneschemaTemplatePro: string | null;
  nodeEnv: string | null;
  gtmContainerId: string | null;
  coraEnabled: boolean | null;
}

let loadPromise: Promise<PublicEnv> | null = null;

/**
 * Fetches public configuration and populates PUBLIC_ENV. Idempotent — concurrent and
 * repeat calls share one in-flight request.
 *
 * Uses bare `fetch` rather than the shared axios client on purpose: this runs before
 * Clerk is initialised, so the client's auth interceptor has no token to attach.
 *
 * @throws if the request fails or the Clerk key is absent. That is deliberate — a
 * silent fallback here is what allowed a development key to ship to production.
 * index.tsx catches this and renders an explicit error instead of a blank page.
 */
export async function loadPublicEnv(): Promise<PublicEnv> {
  if (loadPromise) return loadPromise;

  loadPromise = (async () => {
    const response = await fetch(`${API_BASE_URL}/config`, {
      headers: { Accept: "application/json" },
    });

    if (!response.ok) {
      throw new Error(
        `Failed to load public configuration: GET ${API_BASE_URL}/config returned ${response.status}`,
      );
    }

    const config = (await response.json()) as ConfigResponse;

    PUBLIC_ENV.CLERK_PUBLISHABLE_KEY = config.clerkPublishableKey ?? "";
    PUBLIC_ENV.POSTHOG_KEY = config.posthogKey ?? "";
    PUBLIC_ENV.POSTHOG_HOST = config.posthogHost ?? "";
    PUBLIC_ENV.ONESCHEMA_CLIENT_ID = config.oneschemaClientId ?? "";
    PUBLIC_ENV.ONESCHEMA_TEMPLATE_STARTER = config.oneschemaTemplateStarter ?? "";
    PUBLIC_ENV.ONESCHEMA_TEMPLATE_PRO = config.oneschemaTemplatePro ?? "";
    PUBLIC_ENV.NODE_ENV = config.nodeEnv ?? "";
    PUBLIC_ENV.GTM_CONTAINER_ID = config.gtmContainerId ?? "";
    PUBLIC_ENV.CORA_ENABLED = config.coraEnabled === true;

    if (!PUBLIC_ENV.CLERK_PUBLISHABLE_KEY) {
      throw new Error(
        "CLERK_PUBLISHABLE_KEY is not set on the API. Authentication cannot start. " +
          "Set it in this environment's variables and redeploy.",
      );
    }

    return PUBLIC_ENV;
  })();

  return loadPromise;
}
